import React, { useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../utils/localDate";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportControlBar from "../components/ReportControlBar";
import { API_ENDPOINTS, API_BASE_URL } from "../config/constants";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  soft: "#eef6ff",
};

const today = () => toISO(new Date());
const toISO = (date) => toLocalYmd(date);
const TABLE_CACHE_KEY = "tablelistx";
const TAKEAWAY_STORAGE_KEY = "takeawaySlots";
const TAKEAWAY_FILTER_VALUE = "__takeaway__";

const createDefaultReport = (from = today(), to = today()) => ({
  restaurant: "",
  from,
  to,
  total_tables: 0,
  total_orders: 0,
  total_quantity: 0,
  total_amount: 0,
  table_options: [],
  tables: [],
});

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const isTakeawayKey = (value) => {
  const raw = normalizeText(value);
  return raw === "0" || raw === "-1" || raw.startsWith("takeaway");
};

const extractTakeawayIndex = (value) => {
  const raw = normalizeText(value);
  if (!raw || raw === "takeaway") return null;
  const match = raw.match(/takeaway[\s-]*(\d+)/);
  if (!match) return null;
  const slot = Number.parseInt(match[1], 10);
  return Number.isNaN(slot) ? null : slot;
};

const formatTakeawayLabel = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "0" || raw === "-1" || normalizeText(raw) === "takeaway") {
    return "TakeAway";
  }
  const slot = extractTakeawayIndex(raw);
  return slot ? `TakeAway ${slot}` : "TakeAway";
};

const getTableType = (table) => {
  const rawType = normalizeText(table?.table_type ?? table?.tableType ?? table?.type ?? "");
  if (rawType === "room") return "room";
  if (rawType === "takeaway") return "takeaway";

  const rawValue =
    table?.table_key ??
    table?.tableKey ??
    table?.table_name ??
    table?.display_name ??
    table?.name ??
    table?.table_id ??
    table?.tableid ??
    table?.id ??
    "";

  return isTakeawayKey(rawValue) ? "takeaway" : "table";
};

const getTableOptionValue = (table) => {
  const candidates = [
    table?.table_key,
    table?.table_id,
    table?.tableid,
    table?.sort_order,
    table?.table_name,
    table?.display_name,
    table?.name,
    table?.id,
  ];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    const value = String(candidate).trim();
    if (value) return value;
  }
  return "";
};

const formatTableLabel = (table) => {
  const type = getTableType(table);
  if (type === "takeaway") {
    const source =
      table?.table_name ??
      table?.display_name ??
      table?.table_key ??
      table?.table_id ??
      table?.tableid ??
      "";
    return formatTakeawayLabel(source);
  }

  const base = String(
    table?.table_name ?? table?.display_name ?? table?.table_key ?? table?.table_id ?? ""
  ).trim();
  const prefix = type === "room" ? "Room" : "Table";

  if (!base) return prefix;
  if (base.toLowerCase() === "unassigned") return base;
  if (base.toLowerCase().startsWith(prefix.toLowerCase())) return base;
  return `${prefix} ${base}`;
};

const isTakeawayTable = (table) => getTableType(table) === "takeaway";

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value) => formatStoredMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const TableWiseSalesListPage = () => {
  const [userId, setUserId] = useState(null);
  const [restaurant, setRestaurant] = useState({});
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [selectedTables, setSelectedTables] = useState([]);
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [report, setReport] = useState(createDefaultReport(today(), today()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tableCatalog, setTableCatalog] = useState([]);
  const [hasTakeawaySetup, setHasTakeawaySetup] = useState(false);
  const reportRef = useRef(null);
  const tableDropdownRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
    setRestaurant(user || {});
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fromDate, toDate]);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!tableDropdownRef.current?.contains(event.target)) {
        setTableDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    const syncLocalTableState = () => {
      try {
        const cachedTables = JSON.parse(localStorage.getItem(TABLE_CACHE_KEY) || "[]");
        setTableCatalog(Array.isArray(cachedTables) ? cachedTables : []);
      } catch {
        setTableCatalog([]);
      }

      try {
        const storedSlots = JSON.parse(localStorage.getItem(TAKEAWAY_STORAGE_KEY) || "[]");
        setHasTakeawaySetup(
          Array.isArray(storedSlots) &&
            storedSlots.some((slot) => isTakeawayKey(slot) || normalizeText(slot) === "takeaway")
        );
      } catch {
        setHasTakeawaySetup(false);
      }
    };

    let active = true;
    syncLocalTableState();

    if (!userId) return undefined;

    const hydrateTables = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.LIST_TABLE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId }),
        });
        const json = await res.json();
        if (!res.ok || !Array.isArray(json?.list) || !active) return;
        localStorage.setItem(TABLE_CACHE_KEY, JSON.stringify(json.list));
        setTableCatalog(json.list);
      } catch {
        // ignore table list hydrate failures
      } finally {
        if (active) {
          syncLocalTableState();
        }
      }
    };

    const handleFocus = () => {
      if (!active) return;
      syncLocalTableState();
    };

    hydrateTables();
    window.addEventListener("focus", handleFocus);
    return () => {
      active = false;
      window.removeEventListener("focus", handleFocus);
    };
  }, [userId]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/report-v2/table-wise-sales-list`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: userId,
            fromdate: fromDate,
            todate: toDate,
          }),
        }
      );
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || "Failed to load report");
      }
      setReport({
        ...createDefaultReport(fromDate, toDate),
        ...json,
        table_options: Array.isArray(json?.table_options) ? json.table_options : [],
        tables: Array.isArray(json?.tables) ? json.tables : [],
      });
    } catch (error) {
      toast.error(error.message || "Unable to load report");
      setReport(createDefaultReport(fromDate, toDate));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const tableOptions = useMemo(() => {
    const sourceTables = [
      ...(Array.isArray(tableCatalog) ? tableCatalog : []),
      ...(Array.isArray(report.table_options) ? report.table_options : []),
      ...(Array.isArray(report.tables) ? report.tables : []),
    ];

    const hasTakeawayOption =
      hasTakeawaySetup || sourceTables.some((table) => isTakeawayTable(table));

    const options = [];
    const seen = new Set();

    if (hasTakeawayOption) {
      options.push({
        table_type: "takeaway",
        value: TAKEAWAY_FILTER_VALUE,
        label: "TakeAway",
        sort_order: -1,
      });
      seen.add(TAKEAWAY_FILTER_VALUE);
    }

    sourceTables
      .filter((table) => table && !isTakeawayTable(table))
      .sort((left, right) => {
        const leftSort = Number(left?.sort_order);
        const rightSort = Number(right?.sort_order);
        const leftHasSort = Number.isFinite(leftSort);
        const rightHasSort = Number.isFinite(rightSort);

        if (leftHasSort && rightHasSort && leftSort !== rightSort) return leftSort - rightSort;
        if (leftHasSort !== rightHasSort) return leftHasSort ? -1 : 1;

        return formatTableLabel(left).localeCompare(formatTableLabel(right), "en", {
          sensitivity: "base",
          numeric: true,
        });
      })
      .forEach((table) => {
        const value = getTableOptionValue(table);
        const normalizedValue = normalizeText(value);
        if (!normalizedValue || seen.has(normalizedValue)) return;

        seen.add(normalizedValue);
        options.push({
          ...table,
          table_type: getTableType(table),
          value,
          label: formatTableLabel(table),
        });
      });

    return options;
  }, [hasTakeawaySetup, report.table_options, report.tables, tableCatalog]);

  useEffect(() => {
    if (!selectedTables.length) return;
    const validValues = new Set(tableOptions.map((table) => table.value));
    const nextSelection = selectedTables.filter((value) => validValues.has(value));
    if (nextSelection.length === selectedTables.length) return;
    setSelectedTables(nextSelection);
  }, [selectedTables, tableOptions]);

  const selectedTableLabels = useMemo(
    () =>
      tableOptions
        .filter((table) => selectedTables.includes(table.value))
        .map((table) => table.label),
    [selectedTables, tableOptions]
  );

  const selectedTableLabel = useMemo(() => {
    if (selectedTableLabels.length === 0) return "All tables";
    if (selectedTableLabels.length <= 2) return selectedTableLabels.join(", ");
    return `${selectedTableLabels.slice(0, 2).join(", ")} +${selectedTableLabels.length - 2}`;
  }, [selectedTableLabels]);

  const toggleTableSelection = (value) => {
    setSelectedTables((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const selectAllTables = () => {
    setSelectedTables(tableOptions.map((table) => table.value));
  };

  const clearTableSelection = () => {
    setSelectedTables([]);
  };

  const visibleTables = useMemo(() => {
    const normalizedFilters = selectedTables.map((value) => normalizeText(value));
    const query = normalizeText(search);

    return (report.tables || []).reduce((acc, table) => {
      const tableValue = normalizeText(getTableOptionValue(table));
      const matchesSelectedTable =
        normalizedFilters.length === 0 ||
        normalizedFilters.some((filterValue) => {
          if (filterValue === TAKEAWAY_FILTER_VALUE) {
            return isTakeawayTable(table);
          }

          return [
            tableValue,
            normalizeText(table?.table_key),
            normalizeText(table?.table_name),
            normalizeText(table?.table_id),
            normalizeText(table?.sort_order),
            normalizeText(formatTableLabel(table)),
          ].includes(filterValue);
        });

      if (!matchesSelectedTable) return acc;

      const filteredOrders = (table.orders || []).filter((order) => {
        if (!query) return true;
        const haystack = [
          order?.ordercode,
          order?.payment_mode,
          order?.customer_name,
          order?.waiter_name,
          order?.total_quantity,
          order?.total_amount,
          table?.table_name,
        ]
          .map((item) => String(item ?? ""))
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });

      if (!filteredOrders.length) return acc;

      const totalOrders = filteredOrders.length;
      const totalQuantity = filteredOrders.reduce(
        (sum, order) => sum + Number(order?.total_quantity || 0),
        0
      );
      const totalAmount = filteredOrders.reduce(
        (sum, order) => sum + Number(order?.total_amount || 0),
        0
      );

      acc.push({
        ...table,
        orders: filteredOrders,
        total_orders: totalOrders,
        total_quantity: totalQuantity,
        total_amount: Number(totalAmount.toFixed(2)),
        avg_order_value:
          totalOrders > 0 ? Number((totalAmount / totalOrders).toFixed(2)) : 0,
      });

      return acc;
    }, []);
  }, [report.tables, search, selectedTables]);

  const summary = useMemo(() => {
    const totals = visibleTables.reduce(
      (acc, table) => {
        acc.totalTables += 1;
        acc.totalOrders += Number(table?.total_orders || 0);
        acc.totalQuantity += Number(table?.total_quantity || 0);
        acc.totalAmount += Number(table?.total_amount || 0);
        return acc;
      },
      {
        totalTables: 0,
        totalOrders: 0,
        totalQuantity: 0,
        totalAmount: 0,
      }
    );

    return {
      ...totals,
      totalAmount: Number(totals.totalAmount.toFixed(2)),
    };
  }, [visibleTables]);

  const exportCsv = () => {
    const rows = visibleTables.flatMap((table) =>
      (table.orders || []).map((order, index) => [
        formatTableLabel(table),
        index + 1,
        order?.ordercode || order?.id || "-",
        formatDate(order?.created_at),
        formatTime(order?.created_at),
        order?.waiter_name || "-",
        order?.customer_name || "-",
        order?.payment_mode || "-",
        Number(order?.total_quantity || 0),
        Number(order?.total_amount || 0),
      ])
    );

    downloadCSV(
      `table_wise_sales_list_${fromDate}_to_${toDate}.csv`,
      [
        "Table",
        "#",
        "Bill No",
        "Date",
        "Time",
        "Waiter",
        "Customer",
        "Payment",
        "Qty",
        "Amount",
      ],
      rows
    );
  };

  const exportExcel = () => {
    const rows = [];
    visibleTables.forEach((t) =>
      (t.orders || []).forEach((order, idx) => {
        rows.push([
          t.table_name,
          idx + 1,
          order?.billno || "-",
          order?.date || "-",
          order?.time || "-",
          order?.waiter_name || "-",
          order?.customer_name || "-",
          order?.payment_mode || "-",
          Number(order?.total_quantity || 0),
          Number(order?.total_amount || 0),
        ]);
      })
    );

    downloadExcel(
      `table_wise_sales_list_${fromDate}_to_${toDate}.xlsx`,
      [
        "Table",
        "#",
        "Bill No",
        "Date",
        "Time",
        "Waiter",
        "Customer",
        "Payment",
        "Qty",
        "Amount",
      ],
      rows
    );
  };

  const exportPdf = () =>
    downloadPDF(reportRef.current, `table_wise_sales_list_${fromDate}_to_${toDate}.pdf`);

  const restaurantName =
    report.restaurant ||
    restaurant.restaurant_name ||
    restaurant.restaurantname ||
    restaurant.name ||
    "Restaurant";

  const restaurantAddress =
    restaurant.addressline3 ||
    restaurant.address ||
    restaurant.restaurantaddress ||
    restaurant.city ||
    "";

  const restaurantPhone =
    restaurant.mobile_number ||
    restaurant.mobile ||
    restaurant.phone ||
    restaurant.contactnumber ||
    "";

  const generatedOn = useMemo(
    () =>
      new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    []
  );

  const emptyMessage = search.trim()
    ? "No orders match the current filters."
    : "No table-wise sales found for this range.";

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader title="Table-wise Sales List" subtitle="Sales grouped by table for the selected date range." stats={[{ label: "Tables", value: summary.totalTables }, { label: "Orders", value: summary.totalOrders }, { label: "Quantity", value: summary.totalQuantity }, { label: "Amount", value: formatAmount(summary.totalAmount) }]} />

      <ReportControlBar>
        <FiltersGrid>
          <ReportPeriodPicker fromDate={fromDate} toDate={toDate} onChange={({ from, to }) => { setFromDate(from); setToDate(to); }} />
          <SelectInput ref={tableDropdownRef}>
            <span>Tables:</span>
            <MultiSelectTrigger
              type="button"
              onClick={() => setTableDropdownOpen((prev) => !prev)}
            >
              <MultiSelectValue>{selectedTableLabel}</MultiSelectValue>
              <MultiSelectMeta>
                {selectedTables.length
                  ? `${selectedTables.length} selected`
                  : `${tableOptions.length} available`}
              </MultiSelectMeta>
            </MultiSelectTrigger>
            {tableDropdownOpen && (
              <MultiSelectMenu>
                <MultiSelectActions>
                  <MultiSelectAction type="button" onClick={selectAllTables}>
                    Select all
                  </MultiSelectAction>
                  <MultiSelectAction type="button" onClick={clearTableSelection}>
                    Clear
                  </MultiSelectAction>
                </MultiSelectActions>
                <MultiSelectList>
                  {tableOptions.length === 0 ? (
                    <MultiSelectEmpty>No tables available</MultiSelectEmpty>
                  ) : (
                    tableOptions.map((table) => (
                      <MultiSelectItem key={`${table.value}-${table.label}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={selectedTables.includes(table.value)}
                            onChange={() => toggleTableSelection(table.value)}
                          />
                          <span>{table.label}</span>
                        </label>
                      </MultiSelectItem>
                    ))
                  )}
                </MultiSelectList>
              </MultiSelectMenu>
            )}
          </SelectInput>
          <SearchBox>
            <FaSearch />
            <input
              type="search"
              placeholder="Search bill, waiter, customer, payment"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </SearchBox>
          <PrimaryBtn type="button" onClick={() => fetchReport({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </FiltersGrid>
      </ReportControlBar>

      <Card>
        <div ref={reportRef}>
          <PdfHeaderCard>
            <PdfHeaderRow>
              <div className="left">
                <div className="resto">{restaurantName}</div>
                {restaurantAddress && <div className="line">{restaurantAddress}</div>}
                {restaurantPhone && <div className="line">Phone: {restaurantPhone}</div>}
              </div>
              <div className="right">
                <h2>Table-wise Sales List</h2>
                <div className="line">
                  Period: <strong>{fromDate}</strong> to <strong>{toDate}</strong>
                </div>
                {selectedTableLabels.length > 0 && (
                  <div className="line">
                    Table Filter:{" "}
                    <strong>{selectedTableLabels.join(", ")}</strong>
                  </div>
                )}
                <div className="line">
                  Generated on: <strong>{generatedOn}</strong>
                </div>
              </div>
            </PdfHeaderRow>
          </PdfHeaderCard>

          {loading ? (
            <Center>Loading…</Center>
          ) : visibleTables.length === 0 ? (
            <Center>{emptyMessage}</Center>
          ) : (
            <GroupList>
              {visibleTables.map((table) => (
                <GroupCard key={`${getTableOptionValue(table)}-${table.table_name}`}>
                  <GroupHeader>
                    <div>
                      <GroupTitle>{formatTableLabel(table)}</GroupTitle>
                      <GroupMeta>
                        <span>
                          Type:{" "}
                          {table?.table_type === "room"
                            ? "Room"
                            : table?.table_type === "takeaway"
                            ? "TakeAway"
                            : "Table"}
                        </span>
                        {table?.seat_capacity ? <span>Seats: {table.seat_capacity}</span> : null}
                      </GroupMeta>
                    </div>
                    <GroupStats>
                      <StatBadge>Orders: {table.total_orders}</StatBadge>
                      <StatBadge>Qty: {table.total_quantity}</StatBadge>
                      <StatBadge>Amount: {formatAmount(table.total_amount)}</StatBadge>
                    </GroupStats>
                  </GroupHeader>

                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Bill No</th>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Waiter</th>
                          <th>Customer</th>
                          <th>Payment</th>
                          <th>Qty</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {table.orders.map((order, index) => (
                          <tr key={`${table.table_name}-${order.id}-${index}`}>
                            <td>{index + 1}</td>
                            <td>{order?.ordercode || order?.id || "-"}</td>
                            <td>{formatDate(order?.created_at)}</td>
                            <td>{formatTime(order?.created_at)}</td>
                            <td>{order?.waiter_name || "-"}</td>
                            <td>{order?.customer_name || "-"}</td>
                            <td>{order?.payment_mode || "-"}</td>
                            <td>{Number(order?.total_quantity || 0)}</td>
                            <td>{formatAmount(order?.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={7}>
                            <strong>Table Total</strong>
                          </td>
                          <td>
                            <strong>{table.total_quantity}</strong>
                          </td>
                          <td>
                            <strong>{formatAmount(table.total_amount)}</strong>
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </TableWrap>
                </GroupCard>
              ))}
            </GroupList>
          )}

          <PdfFooter>
            <span>Powered by</span>
            <strong>BillSarthi</strong>
          </PdfFooter>
        </div>

        <Actions>
          <PrimaryBtn type="button" onClick={exportExcel}>
            Excel
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportPdf}>
            PDF
          </PrimaryBtn>
          <PrimaryBtn
            type="button"
            onClick={() =>
              printThermal(reportRef.current, `table_wise_sales_list_${fromDate}_to_${toDate}`)
            }
          >
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportCsv}>
            CSV
          </PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default TableWiseSalesListPage;

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom:16px;padding:26px 28px;border-radius:22px;background:linear-gradient(135deg,#075f9e,#0787c5);box-shadow:0 14px 30px rgba(1,91,154,.17);
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color:#fff;
`;

const Sub = styled.div`
  color:#dff3ff;
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const Pill = styled.span`
  background:rgba(255,255,255,.14);color:#fff;
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border:1px solid rgba(255,255,255,.25);
`;

const FiltersCard = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 12px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  align-items: center;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9fafb;
  color: ${COLOR.text};
  font-weight: 600;

  input {
    border: 0;
    background: transparent;
    outline: none;
    color: ${COLOR.text};
    width: 100%;
  }
`;

const SelectInput = styled.label`
  display: grid;
  align-content: center;
  gap: 6px;
  min-height: 106px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9fafb;
  color: ${COLOR.text};
  font-weight: 600;
  position: relative;
`;

const MultiSelectTrigger = styled.button`
  width: 100%;
  border: 0;
  background: transparent;
  color: ${COLOR.text};
  text-align: left;
  padding: 0;
  cursor: pointer;
  display: grid;
  gap: 2px;
`;

const MultiSelectValue = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
`;

const MultiSelectMeta = styled.span`
  color: ${COLOR.muted};
  font-size: 0.82rem;
  font-weight: 500;
`;

const MultiSelectMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 20;
  background: #fff;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
  overflow: hidden;
`;

const MultiSelectActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${COLOR.line};
  background: ${COLOR.soft};
`;

const MultiSelectAction = styled.button`
  border: 0;
  background: transparent;
  color: ${COLOR.primary};
  font-weight: 700;
  cursor: pointer;
  padding: 0;
`;

const MultiSelectList = styled.div`
  max-height: 240px;
  overflow-y: auto;
`;

const MultiSelectItem = styled.div`
  border-bottom: 1px solid ${COLOR.line};

  &:last-child {
    border-bottom: 0;
  }

  label {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;
    color: ${COLOR.text};
    font-weight: 500;
  }

  input {
    margin: 0;
  }
`;

const MultiSelectEmpty = styled.div`
  padding: 14px 12px;
  color: ${COLOR.muted};
  font-size: 0.9rem;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9fafb;
  color: ${COLOR.muted};

  input {
    border: 0;
    background: transparent;
    outline: none;
    width: 100%;
    color: ${COLOR.text};
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 54px;
  padding: 11px 14px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 18px;
  padding: 14px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
`;

const PdfHeaderCard = styled.div`
  background: linear-gradient(135deg, ${COLOR.soft}, #ffffff);
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 12px;
`;

const PdfHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  .left,
  .right {
    display: grid;
    gap: 4px;
  }

  .resto {
    font-size: 1.15rem;
    font-weight: 900;
    color: ${COLOR.text};
  }

  .line {
    color: ${COLOR.muted};
    font-size: 0.95rem;
  }

  h2 {
    margin: 0;
    color: ${COLOR.text};
    font-size: 1.1rem;
  }
`;

const GroupList = styled.div`
  display: grid;
  gap: 12px;
`;

const GroupCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
`;

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-bottom: 1px solid ${COLOR.line};
  background: #fbfdff;
`;

const GroupTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: ${COLOR.text};
`;

const GroupMeta = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 4px;
  color: ${COLOR.muted};
  font-size: 0.9rem;
`;

const GroupStats = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatBadge = styled.span`
  border-radius: 999px;
  padding: 7px 10px;
  background: ${COLOR.soft};
  border: 1px solid ${COLOR.line};
  color: ${COLOR.text};
  font-size: 0.88rem;
  font-weight: 700;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;

  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${COLOR.line};
    text-align: left;
    color: ${COLOR.text};
    vertical-align: top;
  }

  th {
    background: #f8fafc;
    font-size: 0.88rem;
  }

  tfoot td {
    background: #fcfcfd;
  }
`;

const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 18px 12px;
`;

const PdfFooter = styled.div`
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px dashed ${COLOR.line};
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  color: ${COLOR.muted};
  font-size: 0.92rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
  flex-wrap: wrap;
`;
